import { useCallback, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import Button from 'components/common/Button';
import { routePatterns } from 'router/route-paths';
import { Link } from 'react-router-dom';
import { ArrowForward } from 'assets/icons';
import { animateScroll } from 'react-scroll';
import WarningMessage from 'components/common/WarningMessage';
import { useAppContext } from 'contexts/AppContext';
import { Col, Row } from 'react-flexbox-grid';
import requireValue from 'utils/requireValue';
import s from './PlanForm.module.scss';
import Category from './Category';
import type { TPlanFormValues, TPlanningCategory } from './utils';

interface IPlanFormProps {
  categories: TPlanningCategory[];
  initialValues: TPlanFormValues;
  projectId: string;
}

const theme2ActiveTabClassName: Record<string, string> = {
  primary: s.primaryActiveCategoryTab,
  green: s.greenActiveCategoryTab,
};

const PlanForm = ({ categories, initialValues, projectId }: IPlanFormProps) => {
  const { formatMessage: t } = useIntl();
  const { theme } = useAppContext();
  const [activeCategoryName, setActiveCategoryName] = useState(categories[0]?.name);
  let activeCategory = categories.find((c) => c.name === activeCategoryName);
  const cardRef = useRef<HTMLDivElement>(null);

  if (!activeCategory) {
    [activeCategory] = categories;
  }

  const onCategoryTabClick = useCallback((categoryName: string) => {
    if (cardRef.current) {
      const coordinates = cardRef.current.getBoundingClientRect();
      animateScroll.scrollTo(coordinates.top + window.pageYOffset - 300);
    }
    setActiveCategoryName(categoryName);
  }, []);

  if (!activeCategoryName) {
    return (
      <Row center="xs">
        <Col md={8} lg={6}>
          <WarningMessage
            title={t({ id: 'page_titles.plan' })}
            message={t({ id: 'project_plan.complete_previous_step' })}
            btnLabel={t({ id: 'project_plan.complete_previous_step_btn' })}
            btnLink={routePatterns.projectDetailsCalculator.stringify({ id: projectId })}
          />
        </Col>
      </Row>
    );
  }

  // Without categories (the active name is kept from a former render) this threw on `.name`.
  const shownCategory = requireValue(activeCategory, 'name');

  return (
    <>
      <div className="card">
        <div className={s.categoryTabs}>
          {categories.map((category) => (
            <Button
              key={category.name}
              className={classnames(
                s.categoryTab,
                category.name === shownCategory.name && theme2ActiveTabClassName[theme],
              )}
              onClick={() => onCategoryTabClick(category.name)}
            >
              {t({ id: category.label })}
            </Button>
          ))}
        </div>
        <div className={s.planOverviewBtn}>
          <Link to={routePatterns.plan.stringify({ id: projectId })}>
            <Button endIcon={<ArrowForward />}>{t({ id: 'planning.plan_overview' })}</Button>
          </Link>
        </div>
      </div>
      {activeCategoryName && (
        <div className={s.container}>
          <Category key={shownCategory.name} {...{ initialValues, cardRef, ...shownCategory }} />
        </div>
      )}
    </>
  );
};

export default PlanForm;
